import React from 'react';
import { CheckIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';
import InfoTooltip from '../../../../components/InfoTooltip';
import SsiGuidelineSources from './ssiGuidelineSources';

// Tooltip content explaining why ThinkDivergent uses 3 domains (DSM-5/ICD-11/NICE) rather
// than the 5 domains from the DIVA-5. Provides a non-prescriptive mapping so clinicians
// familiar with DIVA-5 can orient themselves without being told how to record impairment.
const divaComparisonTooltip = (
  <div>
    <p className="font-bold mb-2">About Domains of Functional Impairment</p>
    <p className="mb-2">
      These three domains of functional impairment align with DSM-5, ICD-11, and NICE guidelines,
      which describe ADHD-related impairment in terms of academic, occupational, social, and
      psychological functioning.
    </p>
    <p className="mb-2">
      For clinicians familiar with DIVA-5 and its five domains of functional impairment, the
      following may be a useful point of reference when working through this section:
    </p>
    <ol className="mb-2 space-y-1 pl-1 list-decimal list-inside">
      <li>Work/Education → <span className="font-semibold">Work/Education</span></li>
      <li>Relationships and/or Family → <span className="font-semibold">Social</span></li>
      <li>Social Contacts → <span className="font-semibold">Social</span></li>
      <li>Free Time/Hobby → <span className="font-semibold">Social</span></li>
      <li>Self-Confidence/Self-Image → <span className="font-semibold">Psychological</span></li>
    </ol>
    <p className="mb-2">
      Please use your clinical judgment about where a patient's impairment is most meaningfully captured.
    </p>
    <SsiGuidelineSources />
  </div>
);

export default function SSISideNavbar({ questions, activeQuestionIndex, selections, jumpToQuestion, currentSectionTitle }) {
  const isQuestionCompleted = (question, index) => {
    if (currentSectionTitle === "Symptoms") {
      // For Symptoms section, check if both adulthood and childhood selections are made
      return selections && selections[index]?.adulthood !== null && selections[index]?.childhood !== null;
    } else {
      // For Domains section, check if adulthood are made (childhood not necessary)
      return selections && selections[index]?.adulthood !== null; 
    }
  };

  return (
    <div className="h-1/4 ml-8 mt-6 p-5 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-start gap-2 inline-flex sticky top-8">
      <div className="min-w-24 self-stretch text-gray-500 text-sm font-bold leading-tight mb-2">
        {currentSectionTitle === "Symptoms" ? "Symptoms" : "Domains of Functional Impairment"}
      </div>
      <div className="text-left self-stretch flex-col justify-start items-start gap-1 flex overflow-y-auto">
        {questions.map((question, index) => {
          const isActive = activeQuestionIndex === index;
          const isCompleted = isQuestionCompleted(question, index);
          return (
            <div
              key={index}
              className={`self-stretch h-9 px-3 py-2 ${isActive ? 'font-bold text-gray-900 border-l-2 border-violet-500' : ''} ${isCompleted ? 'bg-emerald-50' : ''} justify-start items-center gap-2 inline-flex cursor-pointer`}
              data-tooltip-id={`ssi-symptom-${index}`}
              onClick={() => jumpToQuestion(index)}
            >
              <div className={`grow shrink basis-0 ${isActive ? 'text-gray-900' : 'text-gray-500'} text-sm font-medium leading-tight`}>
                {question.DSM_symptom_prefix}
              </div>
              {isCompleted && (
                <CheckIcon className="w-4 h-4 text-green-500" aria-hidden="true" />
              )}
              {question?.DSM_symptom && (
                <Tooltip
                  id={`ssi-symptom-${index}`}
                  place="top"
                  style={{
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    backgroundColor: 'rgba(34, 34, 34, 1)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 'bold'
                  }}
                  content={question.DSM_symptom}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Only show the DIVA-5 comparison tooltip on the Domains section, not Symptoms */}
      {currentSectionTitle !== "Symptoms" && (
        <div className="mt-1 pt-2 border-t border-gray-200 flex items-center justify-center gap-1.5 w-full">
          <span className="text-xs text-gray-400 font-medium">About this section</span>
          <InfoTooltip
            content={divaComparisonTooltip}
            label="About Domains of Functional Impairment"
            maxWidth="380px"
            side="right"
            align="end"
          />
        </div>
      )}
    </div>
  );
}
